import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

interface  DatePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;

  /* soporte opcional por si luego usas Date */
  date?: Date;
  onSelect?: (date: Date) => void;
    size?: 'default' | 'compact';
}

export const DatePicker = ({ value, onChange, size = 'default' }: DatePickerProps) => {
  const [showPopover, setShowPopover] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowPopover(false);
      }
    };

    if (showPopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPopover]);

  // Format date for display: "Sáb, 20 Nov"
const formatDisplayDate = (dateStr?: string) => {
  if (!dateStr) return 'Seleccionar fecha';

  const date = new Date(dateStr + 'T00:00:00');
  const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`;
};


  // Get days in month
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return days;
  };

  const handleQuickSelect = (type: 'today' | 'tomorrow') => {
    const date = new Date();
    if (type === 'tomorrow') {
      date.setDate(date.getDate() + 1);
    }
    const dateStr = date.toISOString().split('T')[0];
onChange?.(dateStr);
    setShowPopover(false);
  };

  const handleDateSelect = (day: number) => {
    // Verificar si la fecha es pasada
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const selectedDate = new Date(year, month, day);
    
    // No permitir seleccionar fechas pasadas
    if (selectedDate < today) {
      return;
    }
    
    const dateStr = selectedDate.toISOString().split('T')[0];
onChange?.(dateStr);
    setShowPopover(false);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const isSelectedDate = (day: number) => {
    if (!value || !day) return false;
    const selectedDate = new Date(value + 'T00:00:00');
    return (
      day === selectedDate.getDate() &&
      currentMonth.getMonth() === selectedDate.getMonth() &&
      currentMonth.getFullYear() === selectedDate.getFullYear()
    );
  };

  const isToday = (day: number) => {
    if (!day) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const isPastDate = (day: number) => {
    if (!day) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateToCheck = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return dateToCheck < today;
  };

  const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const weekDays = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  const inputClasses = size === 'compact'
    ? 'w-full px-3 py-2 border border-[#E5E7EB] rounded-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent text-sm cursor-pointer'
    : 'w-full pl-12 pr-4 py-3.5 border border-[#E5E7EB] rounded-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent transition-all cursor-pointer';

  return (
    <div className="relative" ref={popoverRef}>
      <div className="relative">
        {size === 'default' && (
          <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280] pointer-events-none" />
        )}
        <input
          type="text"
          value={formatDisplayDate(value)}
          onClick={() => setShowPopover(!showPopover)}
          readOnly
          className={inputClasses}
        />
      </div>

      {showPopover && (
        <div className="absolute top-full mt-2 bg-white rounded-[16px] border border-[#E5E7EB] shadow-lg p-4 z-50 w-[320px]">
          {/* Quick Select Chips */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => handleQuickSelect('today')}
              className="flex-1 px-4 py-2 bg-[#F7F8FA] hover:bg-[#FF6A2A] hover:text-white text-[#111827] rounded-[14px] text-sm font-medium transition-all border border-[#E5E7EB] hover:border-[#FF6A2A]"
            >
              Hoy
            </button>
            <button
              onClick={() => handleQuickSelect('tomorrow')}
              className="flex-1 px-4 py-2 bg-[#F7F8FA] hover:bg-[#FF6A2A] hover:text-white text-[#111827] rounded-[14px] text-sm font-medium transition-all border border-[#E5E7EB] hover:border-[#FF6A2A]"
            >
              Mañana
            </button>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-[#F7F8FA] rounded-[10px] transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-[#6B7280]" />
            </button>
            <div className="text-sm font-semibold text-[#111827]">
              {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </div>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-[#F7F8FA] rounded-[10px] transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-[#6B7280]" />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Week day headers */}
            {weekDays.map((day, index) => (
              <div
                key={`weekday-${index}`}
                className="text-center text-xs font-semibold text-[#6B7280] py-2"
              >
                {day}
              </div>
            ))}

            {/* Calendar days */}
            {getDaysInMonth(currentMonth).map((day, index) => {
              if (day === null) {
                return <div key={`empty-${index}`} />;
              }

              const selected = isSelectedDate(day);
              const today = isToday(day);
              const past = isPastDate(day);

              return (
                <button
                  key={`day-${index}`}
                  onClick={() => handleDateSelect(day)}
                  className={`
                    aspect-square flex items-center justify-center rounded-[10px] text-sm font-medium transition-all
                    ${selected 
                      ? 'bg-[#FF6A2A] text-white' 
                      : today 
                        ? 'bg-[#FFF5F0] text-[#FF6A2A] border border-[#FF6A2A]'
                        : past
                          ? 'text-[#9CA3AF] cursor-not-allowed'
                          : 'hover:bg-[#F7F8FA] text-[#111827]'
                    }
                  `}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};